// Private parking listing details — compact bottom sheet.
// Contact details are fetched only when the user taps "Contact owner".

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Loader2, MessageCircle, Mail, Navigation } from "lucide-react";
import { toast } from "sonner";
import { fetchListingContact, listingPhotoUrl, PERIOD_LABEL } from "../lib/privateParking";
import { useBackdropDismiss } from "../lib/useBackdropDismiss";

export default function PrivateListingCard({ listing, onClose }) {
  const dismissProps = useBackdropDismiss(!!listing, onClose);
  const [contact, setContact] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setContact(null);
  }, [listing?.id]);

  const revealContact = async () => {
    setLoading(true);
    try {
      const c = await fetchListingContact(listing.id);
      if (!c.whatsapp && !c.email) {
        toast.error("This owner has no contact details.");
      }
      setContact(c);
    } catch {
      toast.error("Could not load the owner's contact.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {listing && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[3400] bg-black/70"
            {...dismissProps}
            data-testid="listing-backdrop"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 280 }}
            className="fixed inset-x-0 bottom-0 z-[3401] bg-[#141416] border-t border-white/10 rounded-t-3xl overflow-hidden max-h-[88vh] overflow-y-auto sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:bottom-6 sm:rounded-3xl sm:border sm:w-[420px] sm:max-w-[92vw]"
            data-testid="listing-card"
          >
            <div className="relative">
              {listing.has_photo && (
                <img
                  src={listingPhotoUrl(listing.id)}
                  alt={listing.title}
                  className="w-full h-40 object-cover"
                  data-testid="listing-photo"
                />
              )}
              <button
                type="button"
                onClick={onClose}
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 border border-white/15 backdrop-blur flex items-center justify-center active:scale-95 transition-transform"
                data-testid="listing-close"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-base md:text-lg font-heading font-semibold text-white leading-tight" data-testid="listing-title">
                  {listing.title}
                </h2>
                <div className="shrink-0 text-right">
                  <div className="font-heading text-lg font-bold text-[#ccff00]" data-testid="listing-price">
                    €{listing.price}
                  </div>
                  <div className="text-[10px] text-zinc-500">{PERIOD_LABEL[listing.period]}</div>
                </div>
              </div>

              <div className="mt-2 flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    listing.available
                      ? "bg-[#ccff00]/15 text-[#ccff00] border border-[#ccff00]/30"
                      : "bg-white/5 text-zinc-400 border border-white/10"
                  }`}
                  data-testid="listing-availability"
                >
                  {listing.available ? "Available" : "Currently unavailable"}
                </span>
              </div>

              {listing.description && (
                <p className="mt-3 text-[13px] text-zinc-400 leading-relaxed" data-testid="listing-description">
                  {listing.description}
                </p>
              )}

              <a
                href={`https://www.google.com/maps/search/?api=1&query=${listing.lat},${listing.lng}`}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-zinc-300 hover:text-white"
                data-testid="listing-directions"
              >
                <Navigation className="w-3.5 h-3.5 text-[#ccff00]" />
                {listing.lat.toFixed(4)}, {listing.lng.toFixed(4)} — open in Maps
              </a>

              {!contact ? (
                <button
                  type="button"
                  onClick={revealContact}
                  disabled={loading}
                  className="mt-4 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
                  data-testid="listing-contact-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
                  Contact owner
                </button>
              ) : (
                <div className="mt-4 space-y-2" data-testid="listing-contact-revealed">
                  {contact.whatsapp && (
                    <a
                      href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(
                        `Hi! I'm interested in your parking space "${listing.title}" on SpotDrop.`
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                      data-testid="listing-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4" />
                      WhatsApp owner
                    </a>
                  )}
                  {contact.email && (
                    <a
                      href={`mailto:${contact.email}?subject=${encodeURIComponent(
                        `SpotDrop — ${listing.title}`
                      )}`}
                      className="w-full h-12 rounded-full bg-white/[0.04] border border-white/15 text-white font-medium text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                      data-testid="listing-email"
                    >
                      <Mail className="w-4 h-4 text-[#ccff00]" />
                      Email owner
                    </a>
                  )}
                </div>
              )}

              <p className="mt-3 text-[10px] text-zinc-500 leading-relaxed flex items-start gap-1.5">
                <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                SpotDrop does not handle payments or contracts. Arrange everything directly with the owner.
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
